import GeoPosition from '@sats-group/icons/24/geoposition';
import Text from '@sats-group/fresh-fitness-ui-lib/react/text';
import React, { useEffect, useRef, useState } from 'react';

import { useMapbox } from 'root/client/hooks/use-mapbox';
import { findNearestLocations } from 'root/shared/nearest-locations';

import { type Map as Props } from './map.types';
import Button from '@sats-group/fresh-fitness-ui-lib/react/button';
import type mapboxgl from 'mapbox-gl';
import interpolate from 'root/shared/interpolate';

// NOTE: These are to make sure when the map is loaded the "zoom" doesn't go outside of Norway.
const maxBounds: [[number, number], [number, number]] = [
  [4.0, 57.9],
  [31.7, 71.3],
];

const Map: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  accessToken,
  closestClub,
  distanceAway,
  findClosest,
  focusedLocationId,
  icon,
  locations,
  options,
  resetMap,
}) => {
  const { mapbox } = useMapbox();

  // NOTE: We want to treat the map differently when it's on smaller screens
  // like only dispalying the closest club instead of the 5 closest when geoLocation is active
  const desktopMediaQuery = '(min-width: 800px)';
  const isDesktopRef = useRef<boolean>(
    typeof window !== 'undefined'
      ? window.matchMedia(desktopMediaQuery).matches
      : true,
  );

  const mapNode = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const geoLocateControlRef = useRef<mapboxgl.GeolocateControl | null>(null);
  const [geoActive, setGeoActive] = useState(false);
  const clubsRef = useRef<{ lng: number; lat: number; popup?: string }[]>([]);
  const initialBoundsRef = useRef<mapboxgl.LngLatBoundsLike | null>(null);
  const [targetBounds, setTargetBounds] =
    useState<mapboxgl.LngLatBoundsLike | null>(null);
  const nearestClubsRef = useRef<
    { popup?: string; distance: number; lng: number; lat: number }[]
  >([]);

  const distanceText = (distance: string) =>
    interpolate(distanceAway, { distance });

  const addControl = (map: mapboxgl.Map, mapbox: typeof mapboxgl) => {
    const geoLocateControl = new mapbox.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      trackUserLocation: true,
      showUserHeading: true,
      showAccuracyCircle: false,
    });

    geoLocateControlRef.current = geoLocateControl;
    map.addControl(geoLocateControl);

    geoLocateControl.on('geolocate', (pos: GeolocationPosition) => {
      setGeoActive(true);

      const user: [number, number] = [
        pos.coords.longitude,
        pos.coords.latitude,
      ];

      // NOTE: Getting the 5 nearest clubs
      const nearest = findNearestLocations({
        locations: clubsRef.current,
        origin: user,
      }).slice(0, 5);

      const others = nearest.map(club => club);
      others.shift();

      nearestClubsRef.current = others;

      const limit = isDesktopRef.current ? 5 : 1;
      const displayList = nearest.slice(0, limit);

      const fit = isDesktopRef.current
        ? new mapbox.LngLatBounds(user, user)
        : new mapbox.LngLatBounds(displayList[0], displayList[0]);
      displayList.forEach(c => fit.extend([c.lng, c.lat]));
      map.fitBounds(fit, { padding: 60, duration: 500, maxZoom: 15 });

      setTargetBounds([
        [fit.getWest(), fit.getSouth()],
        [fit.getEast(), fit.getNorth()],
      ]);

      displayList.map((club, index) => {
        new mapbox.Popup()
          .setLngLat([club.lng, club.lat])
          .setHTML(
            index === 0 && club.popup
              ? `<div className="map__nearest-club"><Text>${closestClub}</Text>${club.popup}<Text>${distanceText(club.distance.toFixed(1))}</Text></div>`
              : `<div className="map__nearest-club">
                ${club.popup}
                <Text>${distanceText(club.distance.toFixed(1))}</Text>
              </div>`,
          )
          .addTo(map);
      });
    });
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const mm = window.matchMedia(desktopMediaQuery);

    const onChange = (e: MediaQueryListEvent) => {
      isDesktopRef.current = e.matches;
    };
    isDesktopRef.current = mm.matches;
    mm.addEventListener('change', onChange);

    const map = mapRef.current;
    const onMapResize = () => {
      isDesktopRef.current = window.matchMedia(desktopMediaQuery).matches;
    };
    map?.on('resize', onMapResize);

    return () => {
      mm.removeEventListener('change', onChange);
      map?.off('resize', onMapResize);
    };
  }, []);

  useEffect(() => {
    if (!mapbox) {
      return;
    }

    mapbox.accessToken = accessToken;

    if (!mapNode.current) {
      return;
    }

    const map = new mapbox.Map({
      container: mapNode.current,
      ...options,
    });

    mapRef.current = map;
    map.setMaxBounds(maxBounds);

    const clubs: { lng: number; lat: number; popup?: string }[] = [];
    const bounds = new mapbox.LngLatBounds();

    locations.forEach(({ id, lat, lng, popup }) => {
      clubs.push({ lng, lat, popup });
      const markerElement = document.createElement('div');
      markerElement.innerHTML = icon;
      markerElement.style.cursor = 'pointer';

      const marker = new mapbox.Marker({
        element: markerElement,
        offset: [-20, -52], // NOTE: We offset with -20 (40px width / 2) so that the marker is center and -52 (height of marker)
      }).setLngLat([lng, lat]);

      if (popup) {
        marker.setPopup(
          new mapbox.Popup({ closeOnClick: false }).setHTML(popup),
        );

        if (focusedLocationId && focusedLocationId === id) {
          marker.togglePopup();
        }
      }

      marker.addTo(map);

      // NOTE: sometimes people like to add new clubs with locations on the other side of the world
      // - we don't want to include these "bad data" instances to our boundary.
      if (
        lng >= maxBounds[0][0] &&
        lng <= maxBounds[1][0] &&
        lat >= maxBounds[0][1] &&
        lat <= maxBounds[1][1]
      ) {
        if (focusedLocationId) {
          if (focusedLocationId === id) {
            bounds.extend([lng, lat]);
          }
        } else {
          bounds.extend([lng, lat]);
        }
      }
    });

    clubsRef.current = clubs;

    if (!bounds.isEmpty()) {
      // NOTE: mapbox-gl crashes if the padding is too much for the rendered map node.
      // We have to subtract one from half to have room for a marker point.
      const maxVerticalPadding =
        Math.ceil(mapNode.current.clientHeight / 2) - 1;
      const maxHorizontalPadding =
        Math.ceil(mapNode.current.clientWidth / 2) - 1;
      const padding = Math.min(maxHorizontalPadding, maxVerticalPadding, 100);
      map.fitBounds(bounds, { padding, duration: 0 });

      const init: mapboxgl.LngLatBoundsLike = [
        [bounds.getWest(), bounds.getSouth()],
        [bounds.getEast(), bounds.getNorth()],
      ];
      initialBoundsRef.current = init;
      setTargetBounds(init);
    }

    if (!focusedLocationId) {
      addControl(map, mapbox);
    }
  }, [mapbox]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !targetBounds) {
      return;
    }

    const maxV =
      Math.ceil((map.getContainer() as HTMLElement).clientHeight / 2) - 1;
    const maxH =
      Math.ceil((map.getContainer() as HTMLElement).clientWidth / 2) - 1;
    const padding = Math.min(maxH, maxV, 100);

    map.fitBounds(targetBounds, { padding, duration: 500, maxZoom: 15 });
  }, [targetBounds]);

  const toggleNearestClubs = () => {
    const map = mapRef.current;
    if (!map || !mapbox) {
      return;
    }

    if (!geoActive) {
      setGeoActive(true);

      if (geoLocateControlRef.current) {
        geoLocateControlRef.current.trigger();
      }
    } else {
      if (geoLocateControlRef.current) {
        map.removeControl(geoLocateControlRef.current);
        geoLocateControlRef.current = null;
        nearestClubsRef.current = [];
      }

      document.querySelectorAll('.mapboxgl-popup').forEach(el => el.remove());

      if (initialBoundsRef.current) {
        setTargetBounds(initialBoundsRef.current);
      }

      addControl(map, mapbox);

      setGeoActive(false);
    }
  };

  return (
    <div className="map-wrapper">
      <div className="map-wrapper__map" ref={mapNode}></div>
      {!focusedLocationId ? (
        <div className="map-wrapper__clubs-near-me">
          <Button
            icon={<GeoPosition />}
            text={!geoActive ? findClosest : resetMap}
            onClick={() => toggleNearestClubs()}
          />
        </div>
      ) : null}
      {nearestClubsRef.current.length ? (
        <div className="map-wrapper__nearest-clubs">
          {nearestClubsRef.current.map(club =>
            club.popup ? (
              <div className="map-wrapper__nearest-club">
                <div dangerouslySetInnerHTML={{ __html: club.popup }}></div>
                <Text>{distanceText(club.distance.toFixed(1))}</Text>
              </div>
            ) : null,
          )}
        </div>
      ) : null}
    </div>
  );
};

export default Map;
